import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DEFAULT_SITE } from '../siteDefaults';
import {
  deleteProduct,
  fetchSite,
  getToken,
  patchProductsSection,
  saveSite,
  updateProduct,
  uploadFile,
} from './api';
import { input, label } from './adminUi';

function stripHtmlPreview(html, maxLen = 120) {
  if (!html || typeof html !== 'string') return '';
  const plain = html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  if (plain.length <= maxLen) return plain;
  return `${plain.slice(0, maxLen - 1)}…`;
}

function ensureProductsShape(site) {
  const d = DEFAULT_SITE.products;
  if (site.products && Array.isArray(site.products.items)) return site;
  return {
    ...site,
    products: {
      sectionLabel: d.sectionLabel,
      sectionTitle: d.sectionTitle,
      intro: d.intro,
      items: [...(d.items || [])],
    },
  };
}

/** Full site shape for PUT /api/site (news + footer must exist on the client too). */
function ensureAdminSiteShape(site) {
  const s = ensureProductsShape(site);
  return {
    ...DEFAULT_SITE,
    ...s,
    news: s.news && typeof s.news === 'object' && !Array.isArray(s.news) ? s.news : DEFAULT_SITE.news,
    footer:
      s.footer && typeof s.footer === 'object' && !Array.isArray(s.footer) ? s.footer : DEFAULT_SITE.footer,
  };
}

export default function ProductsDashboard() {
  const navigate = useNavigate();
  const [site, setSite] = useState(null);
  const siteRef = useRef(null);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(null);

  siteRef.current = site;

  useEffect(() => {
    if (!getToken()) {
      navigate('/admin', { replace: true });
      return;
    }
    fetchSite()
      .then((data) => setSite(ensureAdminSiteShape(data)))
      .catch(() => setSite(ensureAdminSiteShape(JSON.parse(JSON.stringify(DEFAULT_SITE)))));
  }, [navigate]);

  function updateProducts(field, value) {
    setSite((s) => ({
      ...s,
      products: { ...s.products, [field]: value },
    }));
  }

  async function removeProduct(p, rowIndex) {
    if (!window.confirm('Remove this product from the site?')) return;
    setMsg('');
    setErr('');
    setSaving(true);
    try {
      if (p?.id) {
        const data = await deleteProduct(p.id);
        setSite((s) => ensureAdminSiteShape({ ...s, products: data.products }));
        setMsg('Product removed.');
      } else {
        const s = siteRef.current;
        if (!s) throw new Error('No site state');
        const nextSite = {
          ...s,
          products: { ...s.products, items: s.products.items.filter((_, j) => j !== rowIndex) },
        };
        setSite(nextSite);
        const updated = await saveSite(ensureAdminSiteShape(nextSite));
        setSite(ensureAdminSiteShape(updated));
        setMsg('Product removed.');
      }
    } catch (e) {
      setErr(e.message || 'Remove failed');
      try {
        const data = await fetchSite();
        setSite(ensureAdminSiteShape(data));
      } catch {
        /* ignore */
      }
    } finally {
      setSaving(false);
    }
  }

  function rowUploadKey(p, rowIndex) {
    return typeof p.id === 'string' && p.id.trim() ? p.id : `row-${rowIndex}`;
  }

  async function onProductImageUpload(p, rowIndex, file) {
    if (!file) return;
    setMsg('');
    setErr('');
    const key = rowUploadKey(p, rowIndex);
    setUploadingKey(key);
    try {
      const { url } = await uploadFile(file);
      if (p.id) {
        const data = await updateProduct(p.id, { ...p, image: url });
        setSite((s) => ensureAdminSiteShape({ ...s, products: data.products }));
      } else {
        const s = siteRef.current;
        if (!s) throw new Error('No site state');
        const nextItems = s.products.items.map((item, j) =>
          j === rowIndex ? { ...item, image: url } : item
        );
        const updated = await saveSite(
          ensureAdminSiteShape({ ...s, products: { ...s.products, items: nextItems } })
        );
        setSite(ensureAdminSiteShape(updated));
      }
      setMsg('Image uploaded (Cloudinary).');
    } catch (e) {
      setErr(e.message || 'Upload failed');
      try {
        const d = await fetchSite();
        setSite(ensureAdminSiteShape(d));
      } catch {
        /* ignore */
      }
    } finally {
      setUploadingKey(null);
    }
  }

  async function save() {
    const cur = siteRef.current;
    if (!cur?.products) return;
    setMsg('');
    setErr('');
    setSaving(true);
    try {
      const data = await patchProductsSection({
        sectionLabel: cur.products.sectionLabel,
        sectionTitle: cur.products.sectionTitle,
        intro: cur.products.intro,
      });
      setSite((s) => ensureAdminSiteShape({ ...s, products: data.products }));
      setMsg('Saved.');
    } catch (e) {
      setErr(e.message || 'Save failed');
      try {
        const d = await fetchSite();
        setSite(ensureAdminSiteShape(d));
      } catch {
        /* ignore */
      }
    } finally {
      setSaving(false);
    }
  }

  if (!site || !site.products) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-neutral-600">Loading…</div>
    );
  }

  const { sectionLabel, sectionTitle, intro, items } = site.products;

  return (
    <>
      <header className="z-10 flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-neutral-200 bg-white px-4 py-3 md:px-8">
        <div>
          <p className="text-xs font-medium text-neutral-500">Site content</p>
          <h2 className="text-lg font-bold text-neutral-900 md:text-xl">Products</h2>
        </div>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="rounded-lg bg-brand-forest px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save section'}
        </button>
      </header>

      <main className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-y-contain px-4 py-6 md:px-8 md:py-8">
        {msg ? <p className="mb-4 text-sm text-green-700">{msg}</p> : null}
        {err ? <p className="mb-4 text-sm text-red-600">{err}</p> : null}

        <section className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm">
          <h2 className="mb-4 text-lg font-bold text-neutral-900">Products section</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className={label}>
              Section label
              <input
                className={input}
                value={sectionLabel}
                onChange={(e) => updateProducts('sectionLabel', e.target.value)}
              />
            </label>
            <label className={`${label} sm:col-span-2`}>
              Section title
              <input
                className={input}
                value={sectionTitle}
                onChange={(e) => updateProducts('sectionTitle', e.target.value)}
              />
            </label>
            <label className={`${label} sm:col-span-2`}>
              Intro
              <textarea
                className={`${input} min-h-[5rem]`}
                value={intro}
                onChange={(e) => updateProducts('intro', e.target.value)}
              />
            </label>
          </div>

          <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <h3 className="text-base font-semibold text-neutral-800">Products</h3>
            <Link
              to="/admin/dashboard/products/new"
              className="inline-flex items-center justify-center rounded-lg bg-brand-forest px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:opacity-90"
            >
              + New product
            </Link>
          </div>

          {items.length === 0 ? (
            <p className="mt-5 text-center text-sm text-neutral-500">
              No products yet.{' '}
              <Link to="/admin/dashboard/products/new" className="font-medium text-brand-olive hover:underline">
                Create one
              </Link>
            </p>
          ) : (
            <div className="mt-5 overflow-x-auto rounded-xl border border-neutral-200 bg-white">
              <table className="w-full min-w-[min(100%,960px)] border-collapse text-left text-sm">
                <thead>
                  <tr className="border-b border-neutral-200 bg-neutral-50">
                    <th scope="col" className="w-24 px-3 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-600">
                      Image
                    </th>
                    <th scope="col" className="px-3 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-600">
                      Title
                    </th>
                    <th scope="col" className="max-w-[14rem] px-3 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-600">
                      Description
                    </th>
                    <th scope="col" className="w-[1%] whitespace-nowrap px-3 py-3 text-right text-xs font-semibold uppercase tracking-wide text-neutral-600">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((p, i) => {
                    const uploadKey = rowUploadKey(p, i);
                    const rowUploading = uploadingKey === uploadKey;
                    return (
                    <tr key={p.id || i} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50/90">
                      <td className="align-middle px-3 py-2">
                        <div className="flex w-[5.5rem] flex-col gap-1">
                          <div className="flex h-12 w-full shrink-0 items-center justify-center overflow-hidden rounded-md bg-gradient-to-br from-neutral-100 to-neutral-200">
                            {p.image ? (
                              <img src={p.image} alt="" className="h-full w-full object-cover" />
                            ) : (
                              <span className="px-1 text-center text-[10px] font-medium text-neutral-400">
                                No image
                              </span>
                            )}
                          </div>
                          <label
                            className={`cursor-pointer text-center text-[10px] font-semibold text-brand-olive hover:underline ${
                              rowUploading ? 'pointer-events-none opacity-60' : ''
                            }`}
                          >
                            {rowUploading ? 'Uploading…' : 'Upload'}
                            <input
                              type="file"
                              accept="image/*"
                              className="sr-only"
                              disabled={rowUploading}
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                e.target.value = '';
                                onProductImageUpload(p, i, f);
                              }}
                            />
                          </label>
                          <span className="text-center text-[9px] leading-tight text-neutral-400">
                            Cloudinary
                          </span>
                        </div>
                      </td>
                      <td className="max-w-[min(20rem,32vw)] px-3 py-2 align-middle">
                        <span className="line-clamp-2 font-medium text-neutral-900">{p.title?.trim() ? p.title : 'Untitled'}</span>
                        {p.alt ? (
                          <span className="mt-0.5 block truncate text-xs text-neutral-500" title={p.alt}>
                            Alt: {p.alt}
                          </span>
                        ) : null}
                      </td>
                      <td className="max-w-[14rem] px-3 py-2 align-middle text-xs text-neutral-600">
                        <span className="line-clamp-2" title={stripHtmlPreview(p.description, 500)}>
                          {stripHtmlPreview(p.description) || '—'}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 align-middle text-right">
                        <div className="inline-flex flex-wrap items-center justify-end gap-2">
                          <Link
                            to={`/admin/dashboard/products/${encodeURIComponent(p.id)}/edit`}
                            className="rounded-lg border border-neutral-300 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-800 hover:bg-neutral-50"
                          >
                            Edit
                          </Link>
                          <button
                            type="button"
                            className="rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50"
                            onClick={() => removeProduct(p, i)}
                          >
                            Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </>
  );
}
